import React, { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Columns2, Image as ImageIcon, LayoutPanelTop, Search, Users, X } from "lucide-react";
import { toPng } from "html-to-image";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const PALETTE = ["#003A70", "#009ADE", "#FF9E1B", "#C2D500", "#B39A80", "#96DAEA", "#8B9086"];
export const PLAN_LABELS = {
  AC: "Accident plan",
  CI: "Critical illness plan",
  HI: "Hospital indemnity plan",
  OTH: "Other coverage",
};

const MAX_SELECTED = 6;

const slug = (value) => String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function PreVueExplorer({ census, conditions }) {
  const [query, setQuery] = useState("");
  const [plans, setPlans] = useState([]);
  const [group, setGroup] = useState("all");
  const [split, setSplit] = useState(false);
  const [showCensus, setShowCensus] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const chartRef = useRef(null);
  const [selected, setSelected] = useState([]);

  const groups = useMemo(
    () => [...new Set(conditions.map((c) => c.group).filter(Boolean))].sort(),
    [conditions]
  );

  const availablePlans = useMemo(() => {
    const types = new Set(conditions.map((c) => c.condition_type).filter(Boolean));
    return ["AC", "CI", "HI", "OTH"].filter((type) => types.has(type));
  }, [conditions]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return conditions.filter((condition) => {
      const matchesPlan = !plans.length || !condition.condition_type || plans.includes(condition.condition_type);
      const matchesGroup = group === "all" || condition.group === group;
      const matchesSearch = !needle
        || condition.condition.toLowerCase().includes(needle)
        || (condition.category_type || "").toLowerCase().includes(needle);
      return matchesPlan && matchesGroup && matchesSearch;
    }).sort((a, b) => a.condition.localeCompare(b.condition, "en", { sensitivity: "base" }));
  }, [conditions, plans, group, query]);

  const selectedConditions = useMemo(
    () => selected.map((id) => conditions.find((c) => c.id === id)).filter(Boolean),
    [conditions, selected]
  );

  const colourFor = (id) => PALETTE[Math.max(0, selected.indexOf(id)) % PALETTE.length];

  const ageData = useMemo(() => census.by_age_band.map((band) => {
    const row = { category: band.band, employees: band.total };
    selectedConditions.forEach((condition) => {
      const match = condition.by_band.find((entry) => entry.band === band.band);
      row[condition.id] = Number((((match?.male || 0) + (match?.female || 0))).toFixed(3));
    });
    return row;
  }), [census.by_age_band, selectedConditions]);

  const genderData = useMemo(() => ([
    { category: "Male", employees: census.by_gender.male, key: "male" },
    { category: "Female", employees: census.by_gender.female, key: "female" },
  ].map((entry) => {
    const row = { category: entry.category, employees: entry.employees };
    selectedConditions.forEach((condition) => {
      row[condition.id] = Number((condition.by_gender?.[entry.key] || 0).toFixed(3));
    });
    return row;
  })), [census.by_gender, selectedConditions]);

  const combinedData = useMemo(() => ["female", "male"].flatMap((gender) => (
    census.by_age_band.map((band) => {
      const row = {
        category: `${band.band} ${gender === "male" ? "M" : "F"}`,
        employees: band[gender] || 0,
      };
      selectedConditions.forEach((condition) => {
        const match = condition.by_band.find((entry) => entry.band === band.band);
        row[condition.id] = Number((match?.[gender] || 0).toFixed(3));
      });
      return row;
    })
  )), [census.by_age_band, selectedConditions]);

  const toggleCondition = (condition) => {
    setSelected((current) => {
      if (current.includes(condition.id)) return current.filter((id) => id !== condition.id);
      if (current.length >= MAX_SELECTED) {
        toast.error(`Compare up to ${MAX_SELECTED} conditions at a time`);
        return current;
      }
      return [...current, condition.id];
    });
  };

  const downloadChart = async () => {
    if (!chartRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(chartRef.current, { backgroundColor: "#FFFFFF", pixelRatio: 2, skipFonts: true });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `prevue-prevalence-${new Date().toISOString().slice(0, 10)}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Chart image downloaded");
    } catch {
      toast.error("Could not capture the chart. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const censusLoaded = Boolean(census.total_employees);

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-6 md:py-12 lg:px-10" data-testid="analytics-page">
      <header className="mb-6 flex flex-col gap-4 border-b pb-6 lg:flex-row lg:items-end lg:justify-between" style={{ borderColor: "var(--border)" }}>
        <div>
          <div className="eyebrow mb-2">PreVue · Population risk explorer</div>
          <h1 className="font-heading text-4xl font-black leading-none md:text-5xl" style={{ color: "var(--ink)" }} data-testid="analytics-heading">
            Medical prevalence
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed" style={{ color: "var(--muted)" }}>
            {censusLoaded
              ? "Compare expected condition prevalence against the age and gender shape of your covered population."
              : "Condition prevalence per 1 million patient encounters. Ask your administrator to upload your employee census to overlay it on your own population."}
          </p>
          {!censusLoaded && (
            <div className="mt-4 inline-block border px-3 py-2 text-xs" style={{ borderColor: "var(--border)", background: "#F3F3F1", color: "var(--muted)" }} data-testid="analytics-no-census-notice">
              No census on file — showing benchmark prevalence, not your population.
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 border sm:grid-cols-4" style={{ borderColor: "var(--border)" }} data-testid="analytics-context-bar">
          <ContextCell label="Population" value={censusLoaded ? census.total_employees : "No census"} testId="analytics-population" />
          <ContextCell label="Conditions tracked" value={conditions.length} testId="analytics-condition-count" />
          <ContextCell label="Selected" value={`${selected.length}/${MAX_SELECTED}`} testId="analytics-selection-count" />
        </div>
      </header>

      <div className="grid gap-6 xl:grid-cols-[minmax(300px,360px)_minmax(0,1fr)]">
        <aside className="border flex h-[290px] flex-col overflow-hidden self-start sm:h-[400px] xl:h-[640px]" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="prevue-condition-library">
          <div className="border-b p-5 shrink-0" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center justify-between gap-4">
              <div className="eyebrow">Conditions Library</div>
              <div className="flex flex-col items-end gap-1.5">
                <div className="pill pill-solid" data-testid="condition-selected-count">
                  {selected.length}/{MAX_SELECTED} selected
                </div>
                <button type="button" className="btn-ghost !px-2 !py-1 text-xs" onClick={() => setSelected([])}
                        disabled={!selected.length} data-testid="condition-clear-all-button">
                  Clear All
                </button>
              </div>
            </div>
            <label className="relative mt-3 block" htmlFor="condition-search">
              <span className="sr-only">Search conditions</span>
              <Search className="absolute left-2.5 top-2" size={14} style={{ color: "var(--muted)" }} />
              <input
                id="condition-search"
                className="w-full border py-1.5 pl-8 pr-3 text-sm outline-none"
                style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search conditions"
                data-testid="condition-search-input"
              />
            </label>
            <div className="mt-3">
              <label className="field-label">Condition group</label>
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="condition-group-filter">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent className="!rounded-none max-h-72">
                  <SelectItem value="all">All groups ({conditions.length})</SelectItem>
                  {groups.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name} ({conditions.filter((c) => c.group === name).length})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="coverage-plan-filters">
              {availablePlans.map((plan) => (
                <button
                  key={plan}
                  type="button"
                  className="pill"
                  onClick={() => setPlans((current) => current.includes(plan) ? current.filter((item) => item !== plan) : [...current, plan])}
                  style={{
                    background: plans.includes(plan) ? "var(--accent)" : "transparent",
                    color: plans.includes(plan) ? "#FFFFFF" : "var(--ink)",
                  }}
                  data-testid={`coverage-filter-${plan.toLowerCase()}`}
                >
                  {plan}
                </button>
              ))}
            </div>

          </div>
          <div className="px-5 py-2 text-xs border-b shrink-0 flex items-center justify-between gap-3" style={{ borderColor: "var(--border)", color: "var(--muted)" }}>
            <span data-testid="condition-list-count">Showing {filtered.length} of {conditions.length} conditions</span>
            {selected.length >= MAX_SELECTED && (
              <span style={{ color: "var(--accent)" }} data-testid="condition-limit-note">
                Limit reached — deselect one to swap
              </span>
            )}
          </div>
          <div className="flex-1 min-h-[220px] overflow-y-auto" data-testid="condition-list">
            <div className="grid grid-cols-1">
              {filtered.map((condition) => {
                const isChecked = selected.includes(condition.id);
                const atLimit = !isChecked && selected.length >= MAX_SELECTED;
                return (
                  <label
                    key={condition.id}
                    htmlFor={atLimit ? undefined : `condition-${slug(condition.id)}`}
                    onClick={() => { if (atLimit) toast.error(`Compare up to ${MAX_SELECTED} conditions at a time`); }}
                    className={`flex items-start gap-2.5 border-b px-4 py-2.5 text-sm transition-colors duration-200 ${atLimit ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-[#F3F3F1]"}`}
                    style={{
                      borderColor: "var(--border)",
                      background: isChecked ? "#EAF0F5" : "transparent",
                    }}
                    data-testid={`condition-row-${slug(condition.id)}`}
                  >
                    <Checkbox
                      id={`condition-${slug(condition.id)}`}
                      checked={isChecked}
                      disabled={atLimit}
                      onCheckedChange={() => toggleCondition(condition)}
                      aria-label={`Select ${condition.condition}`}
                      data-testid={`condition-select-${slug(condition.id)}`}
                      className="mt-0.5"
                    />
                    {condition.condition_type && (
                      <span className="mt-[3px] shrink-0 text-[10px] font-semibold tracking-wider leading-snug w-6"
                            style={{ color: "var(--muted)" }}
                            title={PLAN_LABELS[condition.condition_type] || condition.condition_type}
                            data-testid={`condition-plan-${slug(condition.id)}`}>
                        {condition.condition_type}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 leading-snug" style={{ color: "var(--ink)" }}>
                      {condition.condition}
                    </span>
                  </label>
                );
              })}
            </div>
            {!filtered.length && (
              <p className="p-5 text-sm" style={{ color: "var(--muted)" }} data-testid="condition-search-empty">
                No condition matches this search.
              </p>
            )}
          </div>
        </aside>

        <section className="min-w-0 space-y-6 self-start" data-testid="prevue-chart-workspace">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="border flex flex-col xl:h-[640px]"
            style={{ borderColor: "var(--border)", background: "var(--surface)" }}
          >
            <div className="flex flex-col gap-4 border-b p-5 shrink-0 lg:flex-row lg:items-start lg:justify-between" style={{ borderColor: "var(--border)" }}>
              <div>
                <div className="eyebrow">Selected comparison</div>
                <h2 className="mt-1 font-heading text-2xl font-bold" style={{ color: "var(--ink)" }} data-testid="prevalence-chart-title">
                  {split ? "Split View" : (censusLoaded ? "Expected Cases by Age Band and Gender" : "Prevalence by Age Band and Gender")}
                </h2>
                <p className="mt-2 text-sm" style={{ color: "var(--muted)" }} data-testid="prevalence-chart-description">
                  {censusLoaded
                    ? `Each color is one selected condition; bars show the cases expected across your covered population. The grey area behind them is ${census.company || "your"} covered lives in each bucket, on the right-hand axis.`
                    : "Each color is one selected condition; bars show prevalence per 1 million patient encounters. The vertical scale adjusts to the conditions you pick."}
                </p>
              </div>
              <div className="flex items-center gap-2 self-start">
                <button
                  type="button"
                  className="btn-ghost flex items-center gap-2 text-xs"
                  onClick={downloadChart}
                  disabled={downloading || !selectedConditions.length}
                  data-testid="chart-download-button"
                >
                  <ImageIcon size={14}/> {downloading ? "Capturing…" : "Save Image"}
                </button>
                {censusLoaded && Boolean(selectedConditions.length) && (
                  <button
                    type="button"
                    className="btn-ghost flex items-center gap-2 text-xs"
                    onClick={() => setShowCensus((value) => !value)}
                    aria-pressed={showCensus}
                    data-testid="chart-census-toggle"
                  >
                    <Users size={14}/> {showCensus ? "Hide Covered Lives" : "Show Covered Lives"}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-ghost flex items-center gap-2 text-xs"
                  onClick={() => setSplit((value) => !value)}
                  data-testid="chart-split-toggle"
                >
                  {split ? <><LayoutPanelTop size={14}/> Combine Charts</> : <><Columns2 size={14}/> Split By Age & Gender</>}
                </button>
              </div>
            </div>

            <div ref={chartRef} className="flex flex-1 min-h-0 flex-col" data-testid="chart-capture-area">
            {!selectedConditions.length ? (
                <div className="px-5 pt-4 shrink-0 flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--ink)" }} data-testid="prevalence-empty-selection">
                  <Activity size={16} style={{ color: "var(--accent)" }} />
                  Select one or more conditions to compare prevalence.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 px-5 pt-4 shrink-0" data-testid="selected-condition-chips">
                  {selectedConditions.map((condition) => (
                    <button
                      key={condition.id}
                      type="button"
                      className="pill gap-2"
                      style={{ borderColor: colourFor(condition.id), color: "var(--ink)" }}
                      onClick={() => toggleCondition(condition)}
                      data-testid={`selected-condition-${slug(condition.id)}`}
                    >
                      <span className="inline-block h-2 w-2" style={{ background: colourFor(condition.id) }} />
                      {condition.condition} <X size={12} />
                    </button>
                  ))}
                </div>
              )}

                {split ? (
                  <div className="grid flex-1 min-h-[360px] gap-6 p-4 md:p-6 lg:grid-cols-2">
                    <ChartPanel
                      title="By age band"
                      data={ageData}
                      conditions={selectedConditions}
                      colourFor={colourFor}
                      axisLabel={censusLoaded ? "Expected cases" : "Per 1M patient encounters"}
                      showCensus={censusLoaded && showCensus && Boolean(selectedConditions.length)}
                      height="100%"
                      testId="prevalence-age-chart"
                    />
                    <ChartPanel
                      title="By gender"
                      data={genderData}
                      conditions={selectedConditions}
                      colourFor={colourFor}
                      axisLabel={censusLoaded ? "Expected cases" : "Per 1M patient encounters"}
                      showCensus={censusLoaded && showCensus && Boolean(selectedConditions.length)}
                      height="100%"
                      testId="prevalence-gender-chart"
                    />
                  </div>
                ) : (
                  <div className="flex-1 min-h-[360px] p-4 md:p-6" data-testid="prevalence-comparison-chart">
                    <ChartPanel
                      title=""
                      data={combinedData}
                      conditions={selectedConditions}
                      colourFor={colourFor}
                      axisLabel={censusLoaded ? "Expected cases (scale adjusts with conditions)" : "Per 1 million patient encounters (scale will adjust with conditions)"}
                      showCensus={censusLoaded && showCensus && Boolean(selectedConditions.length)}
                      height="100%"
                      testId="prevalence-combined-chart"
                    />
                  </div>
                )}
            </div>
          </motion.div>

        </section>
      </div>
    </div>
  );
}

function ChartPanel({ title, data, conditions, colourFor, height = 330, axisLabel, showCensus = false, testId }) {
  const empty = !conditions.length;
  const peak = Math.max(0, ...data.flatMap((row) => conditions.map((c) => Number(row[c.id] || 0))));
  const tick = (value) => {
    if (empty) return new Intl.NumberFormat("en-US").format(value);
    if (peak >= 1e6) return `${(value / 1e6).toFixed(value % 1e6 ? 1 : 0)}M`;
    if (peak >= 1e4) return `${Math.round(value / 1000)}K`;
    const digits = peak >= 100 ? 0 : peak >= 10 ? 1 : 2;
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: digits }).format(value);
  };
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid={testId}>
      {title && <div className="eyebrow mb-3 shrink-0">{title}</div>}
      <div className="min-h-0 flex-1" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 14, right: 12, left: axisLabel ? 8 : -10, bottom: 0 }}>
            <CartesianGrid stroke="rgba(10,28,22,0.09)" vertical={false} />
            <XAxis dataKey="category" tick={{ fill: "#4B5C56", fontSize: 12 }} />
            <YAxis
              yAxisId="cases"
              tick={{ fill: "#4B5C56", fontSize: 12 }}
              tickFormatter={tick}
              domain={empty ? [0, 100] : undefined}
              ticks={empty ? [0, 25, 50, 75, 100] : undefined}
              width={axisLabel ? 86 : 60}
              label={axisLabel ? {
                value: axisLabel, angle: -90, position: "insideLeft",
                style: { fill: "#4B5C56", fontSize: 11, textAnchor: "middle" },
              } : undefined}
            />
            {!empty && (
              <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #D1D5D2" }}
                       formatter={(value) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(value)} />
            )}
            {showCensus && (
              <YAxis
                yAxisId="lives"
                orientation="right"
                tick={{ fill: "#8A9A94", fontSize: 11 }}
                tickFormatter={(value) => new Intl.NumberFormat("en-US", { notation: "compact" }).format(value)}
                width={54}
              />
            )}
            {!empty && <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />}
            {showCensus && (
              <Area
                yAxisId="lives"
                type="monotone"
                dataKey="employees"
                name="Covered lives"
                stroke="#8A9A94"
                strokeWidth={1}
                fill="rgba(10,28,22,0.10)"
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            {conditions.map((condition) => (
              <Bar
                key={condition.id}
                yAxisId="cases"
                dataKey={condition.id}
                name={condition.condition}
                fill={colourFor(condition.id)}
                radius={0}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ContextCell({ label, value, suffix = "", span = "", testId }) {
  return (
    <div className={`border-r px-4 py-3 last:border-r-0 ${span}`} style={{ borderColor: "var(--border)" }} data-testid={testId}>
      <div className="eyebrow text-[9px]">{label}</div>
      <div className="mt-1 font-heading text-lg font-bold" style={{ color: "var(--ink)" }}>
        {value}{suffix}
      </div>
    </div>
  );
}
